<template>
  <div class="detail-header">
    <div class="detail-header-top">
      <!-- Left icon / avatar slot -->
      <div class="detail-header-icon">
        <slot name="icon" />
      </div>

      <!-- Right content -->
      <div class="detail-header-info">
        <div class="detail-header-title-row">
          <h1 class="detail-header-title">{{ title }}</h1>
          <slot name="badge" />
        </div>
        <p v-if="subtitle" class="detail-header-subtitle">{{ subtitle }}</p>
        <div v-if="$slots.meta" class="detail-header-meta">
          <slot name="meta" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
defineProps<{
  title: string
  subtitle?: string
}>()
</script>

<style scoped>
/* ============================================
   DETAIL HEADER
   ============================================ */

.detail-header {
  @apply relative overflow-hidden
         bg-white dark:bg-gray-800
         rounded-2xl border border-gray-200 dark:border-gray-700
         shadow-sm dark:shadow-gray-900/30;
}

/* Subtle gradient overlay */
.detail-header::before {
  content: '';
  @apply absolute inset-0 opacity-[0.04];
  background: linear-gradient(135deg, theme('colors.primary.DEFAULT'), theme('colors.primary.DEFAULT'));
}

/* Left accent stripe */
.detail-header::after {
  content: '';
  @apply absolute left-0 top-4 bottom-4 w-1 rounded-full
         bg-gradient-to-b from-primary to-primary-hover
         dark:from-primary-dark dark:to-primary-dark;
}

.detail-header-top {
  @apply relative z-10 flex flex-col sm:flex-row items-start gap-5 p-6;
}

/* ============================================
   ICON / AVATAR SLOT
   ============================================ */

.detail-header-icon {
  @apply flex-shrink-0;
}

/* ============================================
   INFO
   ============================================ */

.detail-header-info {
  @apply flex-1 min-w-0;
}

.detail-header-title-row {
  @apply flex flex-wrap items-center gap-3 mb-1;
}

.detail-header-title {
  @apply text-xl sm:text-2xl font-bold text-gray-900 dark:text-white;
}

.detail-header-subtitle {
  @apply text-sm text-gray-500 dark:text-gray-400 mb-3;
}

/* ============================================
   META
   ============================================ */

.detail-header-meta {
  @apply flex flex-wrap items-center gap-4;
}
</style>
